/* eslint-disable react/no-unknown-property */
import { Picture, Text, Button, Container, Link, Heading, Grid, GridItem } from '../../components/index.js'
import { Search as SearchIcon, Close as CloseIcon } from '../HeaderNavigation/icons/index.js'
import { forwardRef, useCallback, useMemo, useState, useEffect, useRef } from 'react'
import type { NavigationSearchProps } from './types.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { getLocalizedPath } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'

import { SearchStatus } from './types.js'

import { debounce } from 'es-toolkit'
import { cn, atobID, highlightSearchWord } from '../../helpers/utils.js'

export const canSearch = (target: any) => {
  return !(
    target.metafields?.global?.HideSearch ||
    target.metafields?.seo?.hidden ||
    target.metafields?.seo?.setting?.noindex
  )
}

const NavigationSearch = forwardRef<HTMLDivElement, NavigationSearchProps>(
  ({ data, onSearch, onClose, searchResult, isSearching, keywords, className, ...rest }, ref) => {
    const { locale = 'us', copyWriting } = useAiuiContext()

    const [searchValue, setSearchValue] = useState('')
    const [isComposing, setIsComposing] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    // 自动聚焦到输入框（阻止滚动行为）
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus({ preventScroll: true })
      }
    }, [])

    const debouncedFunction = debounce((value: string) => {
      onSearch(value)
    }, 300)

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isComposing) {
          window.open(getLocalizedPath(`${data?.moreLink}?q=${searchValue.toLowerCase()}`, locale), '_self')
        }
      },
      [isComposing, searchValue, locale, data?.moreLink]
    )

    const searchKeywords = useMemo(() => {
      if (Array.isArray(keywords) && keywords?.length) {
        return keywords.map((keyword: any) => keyword.search_term)
      }
      if (Array.isArray(data?.popularWords) && data?.popularWords?.length) {
        return data?.popularWords.map((word: any) => word.popularWord)
      }
      return []
    }, [keywords, data?.popularWords])

    const getLink = useCallback(
      (item: any) => {
        const type = item.__typename || item.type
        switch (type) {
          case 'Product':
            return getLocalizedPath(`/products${item.path}?q=${searchValue.toLowerCase()}`, locale)
          case 'Article':
            return getLocalizedPath(
              `/blogs/${item.blog.handle}/${item.handle.replace('storefront-', '')}?q=${searchValue.toLowerCase()}`,
              locale
            )
          default:
            break
        }
      },
      [searchValue, locale]
    )

    const searchStatus = useMemo(() => {
      if (!searchValue) {
        // 没有搜索值
        return SearchStatus.Default
      } else if (searchResult?.totalCount) {
        // 有搜索结果
        return SearchStatus.Predict
      } else if (!isSearching) {
        // 没有搜索结果
        return SearchStatus.Empty
      }
    }, [searchResult?.totalCount, searchValue, isSearching])

    return (
      <Container childClassName="!bg-white" {...rest} className={cn('relative', className)} ref={ref}>
        <style>
          {`
            .navigation-search-component input:-webkit-autofill,
            .navigation-search-component input:-webkit-autofill:hover,
            .navigation-search-component input:-webkit-autofill:focus {
              -webkit-box-shadow: 0 0 0 1000px transparent inset !important; /* 背景透明 */
              -webkit-text-fill-color: #000 !important;
              transition: background-color 9999s ease-in-out 0s !important;
              caret-color: #000;
            }
          `}
        </style>
        <div className="navigation-search-component laptop:h-[52px] desktop:h-[60px] desktop:pt-6 flex items-center justify-between gap-4 border-b border-b-gray-200 py-4 text-black">
          <input
            ref={inputRef}
            value={searchValue}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={handleKeyDown}
            onChange={e => {
              setSearchValue(e.target.value)
              if (e.target.value) {
                debouncedFunction(e.target.value)
              }
            }}
            type="text"
            className="flex-1 bg-white text-sm font-bold leading-[1.4] outline-none placeholder:text-[#4A4C56] focus-visible:!outline-none"
            placeholder={data?.placeholder}
          />
          <div className="flex items-center gap-3">
            <SearchIcon className="size-5 cursor-pointer" onClick={() => onSearch(searchValue)} />
            <div className="h-[20px] w-px bg-[#E4E5E6]" />
            <CloseIcon className="size-5 cursor-pointer" onClick={onClose} />
          </div>
        </div>
        <div className="desktop:gap-12 desktop:py-6 flex flex-col gap-6 py-4 text-black">
          {searchStatus === SearchStatus.Predict ? (
            <>
              <div>
                <div className="flex items-center gap-1">
                  <Text html={data?.suggestText} className="text-sm font-bold leading-[1.4] text-[#6D6D6F]" />
                  {searchResult?.items?.length > 6 && (
                    <Button
                      as="a"
                      iconClassName="size-4"
                      href={getLocalizedPath(`${data?.moreLink}?q=${searchValue.toLowerCase()}`, locale)}
                      className="!p-0 !text-sm font-bold leading-[1.2]"
                      variant="link"
                    >
                      {data?.moreText}
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex flex-col">
                  {searchResult?.items
                    ?.filter((item: any) => canSearch(item))
                    .slice(0, 6)
                    ?.map((item: any) => (
                      <Link
                        className="rounded-sidebar-shelf w-full p-4 no-underline hover:bg-[#F5F5F7] hover:text-current"
                        href={getLink(item)}
                        key={item.id}
                      >
                        <Text
                          html={highlightSearchWord(item.title, searchValue)}
                          className="cursor-pointer text-sm font-bold leading-[1.4]"
                        />
                      </Link>
                    ))}
                </div>
              </div>
              <div key="predict-advertising-card">
                <Text html={data?.hotProductsText} className="text-sm font-bold leading-[1.4] text-[#6D6D6F]" />
                <div className="tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 tablet:gap-x-3 tablet:gap-y-4 laptop:gap-4 mt-2 grid grid-cols-1 gap-2">
                  {data?.advertisingBgImg?.url && (
                    <Link asChild={!data?.advertisingLink} href={getLocalizedPath(data?.advertisingLink, locale)}>
                      <div className="rounded-sidebar-shelf relative">
                        <Picture
                          source={data?.advertisingBgImg?.url}
                          className="laptop:h-[120px] h-[114px]"
                          imgClassName="object-cover h-full"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-4">
                          <Heading
                            size={2}
                            html={data?.advertisingTitle || 'Buy in Guide'}
                            className={cn('font-bold text-white', {
                              'text-black': data?.advertisingTheme === 'dark',
                            })}
                          />
                          <Text
                            html={data?.advertisingSubtitle || '20.000mAh'}
                            className={cn('text-sm font-bold text-white', {
                              'text-black': data?.advertisingTheme === 'dark',
                            })}
                          />
                        </div>
                      </div>
                    </Link>
                  )}
                  {searchResult?.items
                    ?.filter((item: any) => item.type.toLowerCase() === 'product')
                    .slice(0, data?.advertisingBgImg?.url ? 7 : 8)
                    ?.map((item: any) => {
                      const variant = item?.variants?.[0]
                      const productImage = `${variant?.image?.url || item?.images?.[0]?.url}`
                      const productName = item?.title || item?.name
                      const productDesc = item?.description
                      const productBadge = item?.badge || ''
                      const listingLink = getLocalizedPath(
                        `/products/${item?.handle}?variant=${atobID(variant?.id as string)}`,
                        locale
                      )
                      return (
                        <SearchProduct
                          productImage={productImage}
                          productName={productName}
                          productDesc={productDesc}
                          productBadge={productBadge}
                          listingLink={listingLink}
                          key={item.id}
                        />
                      )
                    })}
                </div>
              </div>
            </>
          ) : (
            <>
              {searchStatus === SearchStatus.Empty && (
                <Text
                  html={data?.noResultText}
                  as="p"
                  className="border-b border-[#E4E5E6] pb-6 text-sm font-bold leading-[1.4] text-[#6D6D6F]"
                />
              )}
              <div>
                <Text html={data?.popularText} className="text-sm font-bold leading-[1.4] text-[#6D6D6F]" />
                <div className="mt-2">
                  <SearchKeyword data={data} keywords={searchKeywords} locale={locale} />
                  {Array.isArray(data?.popularPages) && !!data?.popularPages?.length && (
                    <Grid className="laptop:gap-4 laptop:mt-4 mt-3 gap-3">
                      {data?.popularPages?.map((item: any) => (
                        <GridItem
                          key={item?.id}
                          className="tablet:col-span-6 laptop:p-3 desktop:p-4 laptop:col-span-3 col-span-12 p-2 hover:bg-[#F5F5F7]"
                        >
                          <a
                            href={getLocalizedPath(`${data?.moreLink}?q=${item?.popularPage.toLowerCase()}`, locale)}
                            className="line-clamp-1 cursor-pointer text-sm font-bold leading-[1.4]"
                          >
                            {item?.popularPage}
                          </a>
                        </GridItem>
                      ))}
                    </Grid>
                  )}
                </div>
              </div>
              <div key="empty-advertising-card">
                <Text html={data?.hotProductsText} className="text-sm font-bold leading-[1.4] text-[#6D6D6F]" />
                <div className="tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 tablet:gap-x-3 tablet:gap-y-4 laptop:gap-4 mt-2 grid grid-cols-1 gap-2">
                  {data?.advertisingBgImg?.url && (
                    <Link asChild={!data?.advertisingLink} href={getLocalizedPath(data?.advertisingLink, locale)}>
                      <div className="rounded-sidebar-shelf relative overflow-hidden [&_img]:hover:scale-110 [&_img]:hover:transition-all [&_img]:hover:duration-500">
                        <Picture
                          source={data?.advertisingBgImg?.url}
                          className="laptop:h-[120px] h-[114px]"
                          imgClassName="object-cover h-full"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-4">
                          <Heading
                            size={2}
                            html={data?.advertisingTitle || 'Buy in Guide'}
                            className={cn('font-bold text-white', {
                              'text-black': data?.advertisingTheme === 'dark',
                            })}
                          />
                          <Text
                            html={data?.advertisingSubtitle || '20.000mAh'}
                            className={cn('text-sm font-bold text-white', {
                              'text-black': data?.advertisingTheme === 'dark',
                            })}
                          />
                        </div>
                      </div>
                    </Link>
                  )}
                  {data?.hotProducts?.slice(0, data?.advertisingBgImg?.url ? 7 : 8)?.map((item: any) => {
                    const productDesc = item?.custom_description
                    const productBadge = item?.badge || ''
                    const listingLink = getLocalizedPath(
                      `/products/${item?.handle}?variant=${atobID(item?.shopify_id as string)}`,
                      locale
                    )
                    return (
                      <SearchProduct
                        productImage={item?.image}
                        productName={item?.name}
                        productDesc={productDesc}
                        productBadge={productBadge}
                        listingLink={listingLink}
                        key={item.shopify_id || item.id}
                      />
                    )
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </Container>
    )
  }
)

NavigationSearch.displayName = 'NavigationSearch'

const SearchProduct = ({
  productImage,
  productName,
  productDesc,
  productBadge,
  className,
  listingLink,
}: {
  productImage: string
  productName: string
  productDesc: string
  productBadge: string
  className?: string
  listingLink: string
}) => {
  return (
    <Link href={listingLink} className="block no-underline hover:text-current">
      <div
        className={cn(
          'rounded-sidebar-shelf flex shrink-0 gap-4 overflow-hidden bg-[#F5F5F7] px-4 py-3 [&_img]:hover:scale-110 [&_img]:hover:duration-500',
          className
        )}
      >
        <div className="shrink-0">
          <img
            src={productImage}
            loading="lazy"
            alt={productName}
            width={90}
            height={90}
            className="size-[96px] object-contain"
          />
        </div>
        <div className="relative">
          <div className=" h-[24px] ">
            {productBadge && (
              <Text
                as="p"
                html={productBadge}
                className="text-brand-0 mb-1 inline-block h-full rounded-full border-[1.6px] border-[#00BEFA] px-[6px] text-sm font-bold !leading-[22px]"
              />
            )}
          </div>
          <div>
            <Text
              className="lg-desktop:h-[44px] lg-desktop:text-base line-clamp-2 h-[40px] text-sm font-bold leading-[1.4]"
              html={productName}
            />
            {productDesc && (
              <Text
                as="p"
                html={productDesc}
                className="lg-desktop:text-sm mt-1 line-clamp-1 text-xs font-bold leading-[1.4] text-[#6D6D6F]"
              />
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}

const SearchKeyword = ({ data, keywords, locale }: { data: any; keywords: any[]; locale: string }) => {
  return (
    <div className="laptop:gap-4 flex flex-wrap gap-3">
      {keywords?.map((keyword: any, index) => (
        <a
          href={getLocalizedPath(`${data?.moreLink}?q=${keyword?.toLowerCase()}`, locale)}
          key={index}
          className="rounded-sidebar-shelf laptop:p-3 desktop:p-4 cursor-pointer p-2 text-sm font-bold leading-[1.4] hover:bg-[#F5F5F7]"
        >
          {keyword}
        </a>
      ))}
    </div>
  )
}

export default withLayout(NavigationSearch)
